// src/components/WishlistPage.jsx

import { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useNavigate } from "react-router-dom";
import BackButton from "./BackButton";
import { getUserProfile, isAuthenticated } from "../utils/auth";

export default function WishlistPage() {
  const { addToCart } = useCart();
  // Read wishlistItems from context so agent updates are reflected immediately
  const { wishlistItems, removeFromWishlist, reloadWishlist } = useWishlist();
  const navigate = useNavigate();
  const profile = getUserProfile();
  const customerId = profile?.customer_id ? String(profile.customer_id) : null;
  const isLoggedIn = isAuthenticated() && customerId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Wishlist API disabled — page reads from WishlistContext (localStorage + chat WS sync)
  useEffect(() => {
    setLoading(false);
    setError(null);
  }, [wishlistItems.length]);

  // ✅ Utility to safely format price
  const formatPrice = (price) => {
    const num = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  // Add to cart, then remove from wishlist — context handles state update optimistically
  const handleAddToCart = (item) => {
    addToCart({
      ...item,
      quantity: 1,
      size: item.size || null,
    });
    const id = item.article_id ?? item.id;
    if (id) {
      removeFromWishlist(id);
    }
  };

  // Remove from wishlist — context handles state update optimistically
  const handleRemoveFromWishlist = (item) => {
    const id = item.article_id ?? item.id;
    if (!id) return;
    removeFromWishlist(id);
  };

  // ✅ Handle product click - navigate to product detail
  const handleProductClick = (item) => {
    const id = item.id ?? item.article_id;
    if (id) {
      navigate(`/item/${id}`);
    }
  };

  // Not logged in: show sign-in message
  if (!isLoggedIn && !loading) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
        <div className="mb-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-24 w-24 mx-auto text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">
          Sign in to view your wishlist
        </h2>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Your wishlist is saved to your account. Please sign in to see your saved items.
        </p>
        <button
          onClick={() => navigate("/")}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
        >
          Continue to Home
        </button>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
        <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        <p className="mt-4 text-gray-600">Loading your wishlist...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => reloadWishlist()}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-16 text-center">
        <div className="mb-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-24 w-24 mx-auto text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">
          Your wishlist is empty
        </h2>
        <p className="text-gray-600 mb-6">
          Start adding items you love to your wishlist
        </p>
        <button
          onClick={() => navigate("/")}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-10">
      <BackButton />

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">My Wishlist</h1>
        <p className="text-gray-600 mt-2">
          {wishlistItems.length} {wishlistItems.length === 1 ? "item" : "items"}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {wishlistItems.map((item) => (
          <div
            key={item.id}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
          >
            {/* Image */}
            <div
              className="relative cursor-pointer"
              onClick={() => handleProductClick(item)}
            >
              <img
                src={item.image || item.image_url || "/placeholder-image.jpg"}
                alt={item.name || item.title}
                className="w-full h-64 object-cover"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/300x400?text=No+Image";
                }}
              />
              {/* Remove from wishlist button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFromWishlist(item);
                }}
                className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-md hover:bg-red-50 transition-colors"
                title="Remove from wishlist"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-red-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </button>
            </div>

            {/* Details */}
            <div className="p-4">
              <h3
                className="font-medium text-gray-800 mb-2 cursor-pointer hover:text-blue-600 line-clamp-2"
                onClick={() => handleProductClick(item)}
              >
                {item.name || item.title}
              </h3>
              
              <div className="flex items-center justify-between mb-4">
                <div className="text-blue-600 font-semibold text-lg">
                  USD {formatPrice(item.price)}
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart(item);
                }}
                className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
              >
                Add to Cart
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

